/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import axios from 'axios';
export const useCompanyBillingStore = defineStore('companyBillingStore', {
  state: () => ({
    saving: false,
    alerts: {},
    // Injected by components via companyBillingStore.alerts = props.alerts
    accountingAccounts: {},
    accounts: [],
    accountingSettings: {},
    accountingServices: {},
    accountingState: {},
    openExpenseModal: false
  }),
  actions: {
    async listAccountingAccounts() {
      try {
        const response = await axios({
          url: `/accounting/accounts`,
          method: 'GET'
        });
        this.$patch({
          accountingAccounts: response.data
        });
        return response.data;
      } catch (error) {
        // QBO Disconnection should not crash the modal or surface to snack bar (QBO connection is not a requirement for this page)
        if (error.response?.status === 400) {
          return {};
        } else {
          this.alerts?.error(error.message || error.response?.data?.message);
          return {};
        }
      }
    },
    async getAccountingSettings() {
      try {
        const response = await axios({
          url: `/accounting/settings`,
          method: 'GET'
        });
        const data = response?.data ?? {};
        this.$patch({
          accountingSettings: data
        });
        return data;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
      }
    },
    async getAccountingServices() {
      try {
        const response = await axios({
          url: `/accounting/services`,
          method: 'GET'
        });
        this.$patch({
          accountingServices: response.data
        });
        return response.data;
      } catch (error) {
        this.alerts?.error(error.message || error.response.data.message);
      }
    },
    async listTaxcodes() {
      try {
        const response = await axios({
          url: `/accounting/taxcodes`,
          method: 'GET'
        });
        return response.data.taxcodes;
      } catch (error) {
        this.alerts?.error(error.message || error.response.data.message);
      }
    },
    async updateCompanySettings(updatePayload) {
      try {
        const response = await axios.put('/me/company', updatePayload);
        return response.data;
      } catch (error) {
        this.alerts?.error(error.message || error.response.data.message);
      }
    },
    async verify(prompt, me) {
      try {
        if (!me.company?.integration?.quickbooks) {
          this.alerts?.error('QuickBooks integration not enabled');
        }
        const status = await axios.get('/accounting');
        if (!status.data.connected) {
          if (prompt) {
            this.alerts?.error('You are disconnected from QuickBooks. Go to Settings to reconnect...');
          } else {
            this.alerts?.error('You are disconnected from QuickBooks');
          }
        }
        this.accountingState.connected = status.data.connected;
        return status.data;
      } catch (error) {
        this.alerts?.error(error.message || error.response.data.message);
      }
    },
    /**
     * Purges accounting data from QuickBooks integration
     * @param {Object|null|undefined} leanLawApi - Optional leanLawApi service (injected via props)
     * @param {Function} leanLawApi.purgeAccounting - Required method when leanLawApi is provided
     * @returns {Promise<Object>} Response data from the purge operation
     * @throws {Error} When leanLawApi is invalid or purge operation fails
     */
    async purgeAccounting(leanLawApi) {
      try {
        // Validate leanLawApi parameter if provided
        if (leanLawApi !== null && leanLawApi !== undefined) {
          if (typeof leanLawApi !== 'object') {
            throw new Error('leanLawApi must be an object');
          }
          if (typeof leanLawApi.purgeAccounting !== 'function') {
            throw new Error('leanLawApi.purgeAccounting must be a function');
          }

          // Use the injected leanLawApi service if available
          const result = await leanLawApi.purgeAccounting();
          return result;
        } else {
          // Use direct axios call when leanLawApi is not available
          const response = await axios({
            method: 'POST',
            url: '/accounting/purge',
            headers: {
              'Content-Type': 'application/json',
              'x-leanlaw-version': 'v1'
            },
            params: {
              timeOffset: new Date().getTimezoneOffset()
            },
            timeout: 30000
          });
          return response.data;
        }
      } catch (error) {
        console.error('Purge accounting error:', error);
        const errorMessage = error.message || error.response?.data?.message || 'Failed to purge accounting data';
        this.alerts?.error(errorMessage);
        throw error;
      }
    },
    combineQuickBooksAccounts(accounts, types) {
      const result = [];
      types.forEach(type => {
        accounts[type]?.map(a => {
          a.label = `${a.name} (${a.type})`;
          result.push(a);
        });
      });
      return result;
    }
  }
});